/**
 * Analytics Service - Abstraction Layer
 * 
 * Provides unified analytics tracking that can be connected to:
 * - Firebase Analytics (when config is available)
 * - Console logging (development)
 * - No-op (when disabled)
 * 
 * Events fail silently - never break app startup or user flows.
 */

// ==================== EVENT DEFINITIONS ====================

export const ANALYTICS_EVENTS = {
  // Rewarded Ads Events
  REWARDED_AD_SHOWN: 'rewarded_ad_shown',
  REWARDED_AD_STARTED: 'rewarded_ad_started',
  REWARDED_AD_COMPLETED: 'rewarded_ad_completed',
  REWARDED_CREDIT_GRANTED: 'rewarded_credit_granted',
  REWARDED_AD_FAILED: 'rewarded_ad_failed',
  REWARDED_DAILY_LIMIT_REACHED: 'rewarded_daily_limit_reached',
  
  // Tarot Events
  TAROT_STARTED: 'tarot_started',
  TAROT_COMPLETED: 'tarot_completed',
  
  // Horoscope Events
  HOROSCOPE_OPENED: 'horoscope_opened',
  
  // Coffee Reading Events
  COFFEE_STARTED: 'coffee_started',
  COFFEE_COMPLETED: 'coffee_completed',
  
  // Compatibility Events
  COMPATIBILITY_STARTED: 'compatibility_started',
  COMPATIBILITY_COMPLETED: 'compatibility_completed',
  
  // Natal Chart Events
  NATAL_CHART_STARTED: 'natal_chart_started',
  NATAL_CHART_COMPLETED: 'natal_chart_completed',
  
  // Hybrid Analysis Events
  HYBRID_ANALYSIS_STARTED: 'hybrid_analysis_started',
  HYBRID_ANALYSIS_COMPLETED: 'hybrid_analysis_completed',
  
  // AI Coach Events
  AI_COACH_MESSAGE_SENT: 'ai_coach_message_sent',
  AI_COACH_MESSAGE_RECEIVED: 'ai_coach_message_received',
  
  // Purchase Events
  PURCHASE_STARTED: 'purchase_started',
  PURCHASE_COMPLETED: 'purchase_completed',
  PURCHASE_FAILED: 'purchase_failed',
  
  // Auth Events
  LOGIN_SUCCESS: 'login_success',
  LOGIN_FAILED: 'login_failed',
  SIGNUP_SUCCESS: 'signup_success',
  SIGNUP_FAILED: 'signup_failed',
  LOGOUT: 'logout',
  
  // Navigation Events
  TAB_VIEWED: 'tab_viewed',
  SCREEN_VIEW: 'screen_view',
  
  // Partner/Growth Hub Events
  PARTNER_LANDING_VIEW: 'partner_landing_view',
  PARTNER_SIGNUP: 'partner_signup'
};

// ==================== ANALYTICS PROVIDER INTERFACE ====================

class AnalyticsProvider {
  constructor() {
    this.isInitialized = false;
    this.isEnabled = false;
    this.debugMode = process.env.NODE_ENV === 'development';
    this.firebaseAnalytics = null;
  }

  /**
   * Initialize analytics provider
   * Currently runs in no-op mode - events are logged to console in dev
   * 
   * To enable Firebase Analytics:
   * 1. Install: yarn add firebase
   * 2. Set env vars: REACT_APP_FIREBASE_*
   * 3. Uncomment Firebase code below
   */
  async initialize() {
    try {
      // No-op mode - Firebase will be enabled later when config is ready
      // Events are tracked via console.log in development mode
      this.isEnabled = false;
      this.isInitialized = true;
      
      if (this.debugMode) {
        console.log('📊 Analytics running in no-op mode (Firebase not configured)');
      }
    } catch (error) {
      // Fail silently - analytics should never break the app
      this.isEnabled = false;
      this.isInitialized = true;
    }
  }

  /**
   * Get Firebase config from environment
   * Returns null if not configured
   */
  _getFirebaseConfig() {
    const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
    const authDomain = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN;
    const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
    const storageBucket = process.env.REACT_APP_FIREBASE_STORAGE_BUCKET;
    const messagingSenderId = process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID;
    const appId = process.env.REACT_APP_FIREBASE_APP_ID;
    const measurementId = process.env.REACT_APP_FIREBASE_MEASUREMENT_ID;

    // Return null if essential config is missing
    if (!apiKey || !projectId || !appId) {
      return null;
    }

    return {
      apiKey,
      authDomain,
      projectId,
      storageBucket,
      messagingSenderId,
      appId,
      measurementId
    };
  }

  /**
   * Track an analytics event
   * Fails silently if analytics is not enabled
   */
  trackEvent(eventName, params = {}) {
    try {
      // Add timestamp to all events
      const enrichedParams = {
        ...params,
        timestamp: new Date().toISOString(),
        platform: this._getPlatform()
      };

      if (this.isEnabled && this.firebaseAnalytics && this._logEvent) {
        this._logEvent(this.firebaseAnalytics, eventName, enrichedParams);
      }

      // Always log in debug mode for development
      if (this.debugMode) {
        console.log(`📊 [Analytics] ${eventName}`, enrichedParams);
      }
    } catch (error) {
      // Fail silently
      if (this.debugMode) {
        console.warn(`⚠️ [Analytics] Event tracking failed: ${eventName}`, error.message);
      }
    }
  }

  /**
   * Set user properties for analytics
   */
  setUserProperties(properties) {
    try {
      if (this.isEnabled && this.firebaseAnalytics && this._setUserProperties) {
        this._setUserProperties(this.firebaseAnalytics, properties);
      }

      if (this.debugMode) {
        console.log('📊 [Analytics] User properties set:', properties);
      }
    } catch (error) {
      // Fail silently
      if (this.debugMode) {
        console.warn('⚠️ [Analytics] Setting user properties failed:', error.message);
      }
    }
  }

  /**
   * Identify user for analytics
   */
  identifyUser(userId, userProperties = {}) {
    try {
      this.setUserProperties({
        user_id: userId,
        ...userProperties
      });
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Get current platform
   */
  _getPlatform() {
    if (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform()) {
      return window.Capacitor.getPlatform(); // 'android' or 'ios'
    }
    return 'web';
  }
}

// ==================== SINGLETON INSTANCE ====================

const analytics = new AnalyticsProvider();

// ==================== PUBLIC API ====================

/**
 * Initialize analytics (call once at app startup)
 */
export const initializeAnalytics = async () => {
  await analytics.initialize();
};

/**
 * Track a custom event
 */
export const trackEvent = (eventName, params = {}) => {
  analytics.trackEvent(eventName, params);
};

/**
 * Set user properties
 */
export const setUserProperties = (properties) => {
  analytics.setUserProperties(properties);
};

/**
 * Identify user
 */
export const identifyUser = (userId, properties = {}) => {
  analytics.identifyUser(userId, properties);
};

// ==================== CONVENIENCE METHODS ====================

// Rewarded Ads
export const trackRewardedAdShown = (adUnitId) => {
  trackEvent(ANALYTICS_EVENTS.REWARDED_AD_SHOWN, { ad_unit_id: adUnitId });
};

export const trackRewardedAdStarted = (adUnitId) => {
  trackEvent(ANALYTICS_EVENTS.REWARDED_AD_STARTED, { ad_unit_id: adUnitId });
};

export const trackRewardedAdCompleted = (adUnitId, creditsEarned) => {
  trackEvent(ANALYTICS_EVENTS.REWARDED_AD_COMPLETED, { 
    ad_unit_id: adUnitId, 
    credits_earned: creditsEarned 
  });
};

export const trackRewardedCreditGranted = (credits, totalCredits) => {
  trackEvent(ANALYTICS_EVENTS.REWARDED_CREDIT_GRANTED, { 
    credits_granted: credits, 
    total_credits: totalCredits 
  });
};

export const trackRewardedAdFailed = (adUnitId, error) => {
  trackEvent(ANALYTICS_EVENTS.REWARDED_AD_FAILED, { 
    ad_unit_id: adUnitId, 
    error: error 
  });
};

export const trackRewardedDailyLimitReached = (userId) => {
  trackEvent(ANALYTICS_EVENTS.REWARDED_DAILY_LIMIT_REACHED, { user_id: userId });
};

// Tarot
export const trackTarotStarted = (spreadType) => {
  trackEvent(ANALYTICS_EVENTS.TAROT_STARTED, { spread_type: spreadType });
};

export const trackTarotCompleted = (spreadType, cardsDrawn) => {
  trackEvent(ANALYTICS_EVENTS.TAROT_COMPLETED, { 
    spread_type: spreadType, 
    cards_drawn: cardsDrawn 
  });
};

// Horoscope
export const trackHoroscopeOpened = (zodiacSign) => {
  trackEvent(ANALYTICS_EVENTS.HOROSCOPE_OPENED, { zodiac_sign: zodiacSign });
};

// Coffee Reading
export const trackCoffeeStarted = () => {
  trackEvent(ANALYTICS_EVENTS.COFFEE_STARTED);
};

export const trackCoffeeCompleted = () => {
  trackEvent(ANALYTICS_EVENTS.COFFEE_COMPLETED);
};

// Compatibility
export const trackCompatibilityStarted = (sign1, sign2) => {
  trackEvent(ANALYTICS_EVENTS.COMPATIBILITY_STARTED, { sign1, sign2 });
};

export const trackCompatibilityCompleted = (sign1, sign2, score) => {
  trackEvent(ANALYTICS_EVENTS.COMPATIBILITY_COMPLETED, { sign1, sign2, score });
};

// Natal Chart
export const trackNatalChartStarted = () => {
  trackEvent(ANALYTICS_EVENTS.NATAL_CHART_STARTED);
};

export const trackNatalChartCompleted = () => {
  trackEvent(ANALYTICS_EVENTS.NATAL_CHART_COMPLETED);
};

// Hybrid Analysis
export const trackHybridAnalysisStarted = () => {
  trackEvent(ANALYTICS_EVENTS.HYBRID_ANALYSIS_STARTED);
};

export const trackHybridAnalysisCompleted = () => {
  trackEvent(ANALYTICS_EVENTS.HYBRID_ANALYSIS_COMPLETED);
};

// AI Coach
export const trackAICoachMessageSent = () => {
  trackEvent(ANALYTICS_EVENTS.AI_COACH_MESSAGE_SENT);
};

export const trackAICoachMessageReceived = () => {
  trackEvent(ANALYTICS_EVENTS.AI_COACH_MESSAGE_RECEIVED);
};

// Purchases
export const trackPurchaseStarted = (productId, price, currency = 'TRY') => {
  trackEvent(ANALYTICS_EVENTS.PURCHASE_STARTED, { 
    product_id: productId, 
    price, 
    currency 
  });
};

export const trackPurchaseCompleted = (productId, price, currency = 'TRY', transactionId) => {
  trackEvent(ANALYTICS_EVENTS.PURCHASE_COMPLETED, { 
    product_id: productId, 
    price, 
    currency, 
    transaction_id: transactionId 
  });
};

export const trackPurchaseFailed = (productId, error) => {
  trackEvent(ANALYTICS_EVENTS.PURCHASE_FAILED, { 
    product_id: productId, 
    error 
  });
};

// Auth
export const trackLoginSuccess = (method = 'email') => {
  trackEvent(ANALYTICS_EVENTS.LOGIN_SUCCESS, { method });
};

export const trackLoginFailed = (method = 'email', error) => {
  trackEvent(ANALYTICS_EVENTS.LOGIN_FAILED, { method, error });
};

export const trackSignupSuccess = (method = 'email', partnerId = null) => {
  trackEvent(ANALYTICS_EVENTS.SIGNUP_SUCCESS, { method, partner_id: partnerId });
};

export const trackSignupFailed = (method = 'email', error) => {
  trackEvent(ANALYTICS_EVENTS.SIGNUP_FAILED, { method, error });
};

export const trackLogout = () => {
  trackEvent(ANALYTICS_EVENTS.LOGOUT);
};

// Navigation
export const trackTabViewed = (tabName) => {
  trackEvent(ANALYTICS_EVENTS.TAB_VIEWED, { tab_name: tabName });
};

export const trackScreenView = (screenName) => {
  trackEvent(ANALYTICS_EVENTS.SCREEN_VIEW, { screen_name: screenName });
};

// Partner Events
export const trackPartnerLandingView = (partnerId, slug) => {
  trackEvent(ANALYTICS_EVENTS.PARTNER_LANDING_VIEW, { partner_id: partnerId, slug });
};

export const trackPartnerSignup = (partnerId, campaignId) => {
  trackEvent(ANALYTICS_EVENTS.PARTNER_SIGNUP, { partner_id: partnerId, campaign_id: campaignId });
};

export default analytics;
